<template>
  <div :class="{ mobile: isMobile }">
    <ModalContainer
      v-if="isShowMismatchModal && !isAS0Mismatch"
      :modalTitle="`${type} mismatch for ${prefix}`"
      :isCloseWhenClickOutside="isCloseWhenClickOutside"
      closeBtnText="Close"
      @confirm="closeMismatchModal"
      @closeModal="closeMismatchModal"
    >
      <template v-if="type === 'ROA'" slot="body">
        <h6 v-if="isTwoReasons">Two possible reasons for the mismatch:</h6>
        <div v-if="mismatchASInROAList.length" class="reason">
          <div>
            <div class="reason-title">
              <div class="reason-title__left">
                Reason<span v-if="isTwoReasons"> 1</span>:
              </div>
              <div class="reason-title__right">
                The origin AS in the BGP announcements does not match the origin
                AS in the corresponding ROA (Route Origin Authorization).
              </div>
            </div>
            <div class="reason-description">
              <div class="reason-description__left">
                <span>Origin AS in <strong>BGP</strong> is: </span>
                <div>
                  <span class="warning">{{ mismatchROAOriginASInBGP }}</span>
                </div>
              </div>
              <div class="reason-description__right">
                <span v-if="mismatchASInROAList.length === 1"
                  >Origin AS in <strong>ROA</strong> is:
                </span>
                <span v-if="mismatchASInROAList.length > 1"
                  >Origin AS in <strong>ROAs</strong> are:
                </span>
                <div v-for="(item, index) in mismatchASInROAList" :key="index">
                  <span class="warning">AS{{ item.asn }}</span
                  ><span>
                    ({{ item.prefix }}, /{{ item.min_length }} - /{{
                      item.max_length
                    }})</span
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="actions">
            <div class="actions-title">Required actions:</div>
            <ul>
              <li>
                <p class="actions-description">
                  If you did not expect this origin AS in BGP, review your
                  routing configurations to evaluate if there is a
                  misconfiguration or a BGP prefix hijack.
                  <span
                    class="link"
                    @click="isExpandLearnMore = !isExpandLearnMore"
                  >
                    Learn more about BGP hijacking.
                  </span>
                  <fa
                    icon="chevron-down"
                    :class="{ 'fa-rotate-180': isExpandLearnMore }"
                    @click="isExpandLearnMore = !isExpandLearnMore"
                  ></fa>
                </p>
                <transition name="learn-more">
                  <LearnMoreAboutBGP
                    v-if="isExpandLearnMore"
                    class="learn-more"
                  ></LearnMoreAboutBGP>
                </transition>
              </li>
              <li>
                <p class="actions-description">
                  If you did not expect this origin AS in the ROA, review the
                  ROA for this prefix.
                  <!-- <span
                    class="link"
                    @click="navigateToRoutingManagementDashboard"
                    >Routing Management dashboard.
                    <fa icon="external-link-alt"></fa>
                  </span> -->
                </p>
              </li>
            </ul>
          </div>
        </div>
        <!-- Reason 2 -->
        <div v-if="mismatchLengthInROAList.length" class="reason">
          <div>
            <div class="reason-title">
              <div class="reason-title__left">
                Reason<span v-if="isTwoReasons"> 2</span>:
              </div>
              <div class="reason-title__right">
                The prefix length seen in BGP does not match with the ROA
                maxlength.
              </div>
            </div>
            <div class="reason-description">
              <div class="reason-description__left">
                <span>Length in <strong>BGP</strong>: </span>
                <div>
                  <span class="warning">{{ mismatchLengthInBGP }}</span>
                </div>
              </div>
              <div class="reason-description__right">
                <span>
                  Scope in <strong>ROA </strong
                  ><fa
                    icon="info-circle"
                    class="icon__info"
                    v-tooltip.top="{
                      content:
                        'Legal scope, constrained by ROA’s prefix and maximum length.',
                      classes: 'roa-info__tooltip'
                    }"
                    @click="toggleIsShowTooltip"
                    v-click-outside="hideTooltip"
                  ></fa>
                  <div v-if="isShowTooltip" class="roa-info">
                    Legal scope, constrained by ROA’s prefix and maximum length.
                  </div>
                  :
                </span>
                <div
                  v-for="(item, index) in mismatchLengthInROAList"
                  :key="index"
                >
                  <span class="warning"
                    >/{{ item.min_length }} - /{{ item.max_length }}</span
                  >
                  <span> ({{ item.prefix }} - AS{{ item.asn }}) </span>
                </div>
              </div>
            </div>
          </div>
          <div class="actions">
            <div class="actions-title">Required actions:</div>
            <ul>
              <li>
                <p class="actions-description">
                  If you did not expect a route with this length, review your
                  routing configuration to evaluate if there is a
                  misconfiguration or a BGP prefix hijack.
                  <span
                    class="link"
                    @click="isExpandLearnMore = !isExpandLearnMore"
                  >
                    Learn more about BGP hijacking.
                  </span>
                  <fa
                    icon="chevron-down"
                    :class="{ 'fa-rotate-180': isExpandLearnMore }"
                    @click="isExpandLearnMore = !isExpandLearnMore"
                  ></fa>
                </p>
                <transition name="learn-more">
                  <LearnMoreAboutBGP
                    v-if="isExpandLearnMore"
                    class="learn-more"
                  ></LearnMoreAboutBGP>
                </transition>
              </li>
              <li>
                <p class="actions-description">
                  If you did not expect this max length, review the ROAs for
                  this prefix.
                  <!-- <span
                    class="link"
                    @click="navigateToRoutingManagementDashboard"
                    >Routing Management dashboard.
                    <fa icon="external-link-alt"></fa>
                  </span> -->
                </p>
              </li>
            </ul>
          </div>
        </div>
      </template>
      <template v-if="type === 'Route object'" slot="body">
        <div class="reason">
          <div>
            <div class="reason-title">
              <div class="reason-title__left">Reason:</div>
              <div class="reason-title__right">
                The origin AS in the BGP announcements does not match the origin
                AS in the corresponding route object(s) in APNIC's IRR.
              </div>
            </div>
            <div class="reason-description">
              <div class="reason-description__left">
                <span>Origin AS in <strong>BGP</strong> is: </span>
                <div>
                  <span class="warning">{{
                    mismatchRouteObjectOriginASInBGP
                  }}</span>
                </div>
              </div>
              <div class="reason-description__right">
                <span v-if="mismatchRouteObjectList.length === 1"
                  >Origin AS in <strong>route object</strong> is:
                </span>
                <span v-else
                  >Origin AS in <strong>route objects</strong> are:
                </span>
                <div
                  v-for="(item, index) in mismatchRouteObjectList"
                  :key="index"
                >
                  <span class="warning">AS{{ item.asn }}</span
                  ><span> ({{ item.prefix }})</span>
                </div>
              </div>
            </div>
          </div>
          <div class="actions">
            <div class="actions-title">Required actions:</div>
            <ul>
              <li>
                <p class="actions-description">
                  If you did not expect this origin AS in BGP, review your
                  routing configurations to evaluate if there is a
                  misconfiguration or a BGP prefix hijack.
                  <span
                    class="link"
                    @click="isExpandLearnMore = !isExpandLearnMore"
                  >
                    Learn more about BGP hijacking.
                  </span>
                  <fa
                    icon="chevron-down"
                    :class="{ 'fa-rotate-180': isExpandLearnMore }"
                    @click="isExpandLearnMore = !isExpandLearnMore"
                  ></fa>
                </p>
                <transition name="learn-more">
                  <LearnMoreAboutBGP
                    v-if="isExpandLearnMore"
                    class="learn-more"
                  ></LearnMoreAboutBGP>
                </transition>
              </li>
              <li>
                <p class="actions-description">
                  If you did not expect this origin AS in the route object,
                  review the route object for this prefix.
                  <!-- <span
                    class="link"
                    @click="navigateToRoutingManagementDashboard"
                    >Routing Management dashboard.
                    <fa icon="external-link-alt"></fa>
                  </span> -->
                </p>
              </li>
            </ul>
          </div>
        </div>
      </template>
    </ModalContainer>
    <ModalContainer
      v-if="isShowMismatchModal && isAS0Mismatch"
      :modalTitle="`ROA mismatch for AS0`"
      :isCloseWhenClickOutside="isCloseWhenClickOutside"
      closeBtnText="Close"
      @confirm="closeMismatchModal"
      @closeModal="closeMismatchModal"
    >
      <template slot="body">
        <div class="reason">
          <div>
            <div class="reason-title">
              <div class="reason-title__left">
                Reason:
              </div>
              <div class="reason-title__right">
                This BGP route has clashed with an AS0 ROA protecting your
                addresses.
              </div>
            </div>
          </div>
          <div class="bgp-route">
            <div class="bgp-route__title">
              <span>BGP route:</span>
            </div>
            <div>
              <span class="warning">{{
                `${mismatchModalData.data.route.prefix} - AS${
                  mismatchModalData.data.route.asn
                }`
              }}</span>
            </div>
          </div>
          <div class="actions">
            <div class="actions-title">Required actions:</div>
            <ul>
              <li>
                <p class="actions-description">
                  If you did not expect this origin AS in BGP, review your
                  routing configurations to evaluate if there is a
                  misconfiguration or a BGP prefix hijack.
                  <span
                    class="link"
                    @click="isExpandLearnMore = !isExpandLearnMore"
                  >
                    Learn more about BGP hijacking.
                  </span>
                  <fa
                    icon="chevron-down"
                    :class="{ 'fa-rotate-180': isExpandLearnMore }"
                    @click="isExpandLearnMore = !isExpandLearnMore"
                  ></fa>
                </p>
                <transition name="learn-more">
                  <LearnMoreAboutBGP
                    v-if="isExpandLearnMore"
                    class="learn-more"
                  ></LearnMoreAboutBGP>
                </transition>
              </li>
              <li>
                <p class="actions-description">
                  If you want this origin AS to announce this route, you will
                  need to make a ROA to explicitly override the AS0 ROA covering
                  the space.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </template>
    </ModalContainer>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import * as vClickOutside from 'v-click-outside';
import ModalContainer from '../ui/ModalContainer.vue';
import { MISMATCH_TYPE } from '../../constants/routingStatusConstant';
import LearnMoreAboutBGP from './LearnMoreAboutBGP.vue';

export default {
  name: 'MismatchInfoModal',
  components: {
    ModalContainer,
    LearnMoreAboutBGP
  },
  directives: {
    clickOutside: vClickOutside.directive
  },
  data() {
    return {
      isCloseWhenClickOutside: true,
      isExpandLearnMore: false,
      isShowTooltip: false
    };
  },
  computed: {
    ...mapGetters(['isMobile']),
    ...mapState('routingStatusStore', [
      'isShowMismatchModal',
      'mismatchModalData'
    ]),
    ...mapState('dataStore', ['selectedAccount']),
    type() {
      return this.mismatchModalData.type === MISMATCH_TYPE.ROA
        ? 'ROA'
        : 'Route object';
    },
    prefix() {
      return this.mismatchModalData.data.route.prefix;
    },
    mismatchRouteObjectList() {
      if (this.mismatchModalData.type === MISMATCH_TYPE.ROA) return [];

      return this.mismatchModalData.data.route_object_info
        ? this.mismatchModalData.data.route_object_info.route_object_origins
        : [];
    },
    mismatchRouteObjectOriginASInBGP() {
      if (this.mismatchModalData.type === MISMATCH_TYPE.ROA) return '';

      return this.mismatchModalData.data.route_object_info
        ? `AS${this.mismatchModalData.data.route_object_info.bgp_origin_as}`
        : '';
    },
    isTwoReasons() {
      if (
        this.mismatchModalData.type === MISMATCH_TYPE.ROA &&
        this.mismatchModalData.data.roa_info.unmatched_as.length &&
        this.mismatchModalData.data.roa_info.unmatched_length.length
      )
        return true;

      return false;
    },
    mismatchASInROAList() {
      if (this.mismatchModalData.type === MISMATCH_TYPE.ROUTE_OBJECT) return [];

      return this.mismatchModalData.data.roa_info.unmatched_as.length
        ? this.mismatchModalData.data.roa_info.unmatched_as
        : [];
    },
    mismatchROAOriginASInBGP() {
      if (this.mismatchModalData.type === MISMATCH_TYPE.ROUTE_OBJECT) return '';

      return `AS${this.mismatchModalData.data.route.asn}`;
    },
    mismatchLengthInBGP() {
      if (this.mismatchModalData.type === MISMATCH_TYPE.ROUTE_OBJECT) return '';

      return this.mismatchModalData.data.route.prefix
        ? `/${this.mismatchModalData.data.route.prefix.split('/')[1]}`
        : '';
    },
    mismatchLengthInROAList() {
      if (this.mismatchModalData.type === MISMATCH_TYPE.ROUTE_OBJECT) return [];

      return this.mismatchModalData.data.roa_info.unmatched_length.length
        ? this.mismatchModalData.data.roa_info.unmatched_length
        : [];
    },
    isAS0Mismatch() {
      if (this.mismatchModalData.type === MISMATCH_TYPE.ROUTE_OBJECT)
        return false;

      // Only show AS0 modal when every item in unmatched_as is with asn0
      if (
        this.mismatchModalData.data.roa_info &&
        this.mismatchModalData.data.roa_info.unmatched_as &&
        this.mismatchModalData.data.roa_info.unmatched_as.length
      ) {
        const isOnlyAS0 = this.mismatchModalData.data.roa_info.unmatched_as.every(
          item => item.asn === 0
        );

        if (isOnlyAS0) return true;
      }

      // Only show AS0 modal when every item in unmatched_length is with asn0
      if (
        this.mismatchModalData.data.roa_info &&
        this.mismatchModalData.data.roa_info.unmatched_length &&
        this.mismatchModalData.data.roa_info.unmatched_length.length
      ) {
        const isOnlyAS0 = this.mismatchModalData.data.roa_info.unmatched_length.every(
          item => item.asn === 0
        );

        if (isOnlyAS0) return true;
      }

      return false;
    }
  },
  methods: {
    ...mapMutations('routingStatusStore', ['set_isShowMismatchModal']),
    closeMismatchModal() {
      this.set_isShowMismatchModal(false);
    },
    navigateToRoutingManagementDashboard() {
      const accountName = this.selectedAccount.account;
      window.open(
        `https://${
          process.env.VUE_APP_ROUTING_MANAGEMENT_DASHBOARD
        }/${accountName}/resources/index.html`,
        '_blank'
      );
    },
    toggleIsShowTooltip() {
      if (!this.isMobile) return;

      this.isShowTooltip = !this.isShowTooltip;
    },
    hideTooltip() {
      this.isShowTooltip = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.reason:nth-of-type(2) {
  border-top: 1px solid $text-grey;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
}

.reason {
  .reason-title {
    display: flex;
    justify-self: space-between;
    align-items: flex-start;

    &__left {
      flex: 0 0 auto;
      margin-right: 1rem;
      font-weight: 600;
    }
  }

  .reason-description {
    display: flex;
    justify-self: flex-start;
    margin: 1rem 0;

    &__left {
      margin-right: 4rem;
    }

    &__right {
      & > span {
        position: relative;
      }

      .icon__info {
        color: $apnic-blue;
        cursor: pointer;
      }

      .roa-info {
        display: none;
        position: absolute;
        top: -3.5rem;
        left: 6rem;
        width: calc(100vw - 9rem);
        font-size: 0.8rem;
        padding: 0.5rem;
        padding-right: 0;
        background-color: rgba($color: #000000, $alpha: 0.7);
        color: #fff;
      }
    }
  }
}

.actions {
  margin-top: 1.5rem;
  .actions-title {
    font-weight: 600;
  }

  ul {
    margin: 1rem;

    li {
      margin-bottom: 1rem;

      .actions-description {
        line-height: 1.5;
        margin: 0;

        .link {
          border-bottom: 1px solid $text-black;
          cursor: pointer;
        }
        svg {
          cursor: pointer;
          transition: transform 0.5s;
        }
      }

      .learn-more {
        padding-top: 1rem;
        transition: all 0.4s;
      }

      .learn-more-enter,
      .learn-more-leave-to {
        max-height: 0;
        opacity: 0;
      }

      .learn-more-enter-to,
      .learn-more-leave {
        max-height: 100rem;
        opacity: 1;
      }

      .actions-link {
        display: flex;
        justify-content: flex-end;
        align-items: center;

        margin-bottom: 0.5rem;
        color: $text-light-blue;

        span {
          padding: 0.5rem 0;
          cursor: pointer;
        }

        .icon-wrapper {
          padding-left: 0.5rem;
          cursor: pointer;
        }
      }
    }
  }
}

.warning {
  color: $warning-red;
}

.bgp-route {
  margin-top: 1.5rem;

  &__title {
    font-weight: 600;
  }
}

.mobile {
  .reason {
    .reason-title {
      &__left {
        margin-right: 1rem;
      }
    }

    .reason-description {
      flex-direction: column;

      &__left {
        margin-bottom: 1rem;
      }

      &__right {
        .roa-info {
          display: block;
        }
      }
    }
  }

  .actions {
    ul {
      li {
        .actions-description {
          .link {
            padding-top: 0.5rem;
            display: inline-block;
          }
        }
      }
    }
  }
}
</style>
